import React, { useState, useRef } from 'react';
import { Send, Star, CheckCircle, AlertCircle, ImagePlus, X } from 'lucide-react';
import { submitBetaFeedback, uploadFeedbackImage, BetaFeedback } from '../utils/supabase';
import './FeedbackForm.css';

const categories = [
  { value: 'general', label: 'General Feedback' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'ui', label: 'UI/Design' },
  { value: 'performance', label: 'Performance' },
] as const;

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

const FeedbackForm: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [category, setCategory] = useState<BetaFeedback['category']>('general');
  const [comments, setComments] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (rating === 0) {
      setErrorMessage('Please select a rating');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      let image_url: string | undefined;
      if (imageFile) {
        image_url = await uploadFeedbackImage(imageFile);
      }

      await submitBetaFeedback({
        name,
        email,
        rating,
        category,
        comments,
        image_url,
      });
      setStatus('success');
      // Reset form
      setName('');
      setEmail('');
      setRating(0);
      setCategory('general');
      setComments('');
      setImageFile(null);
      setImagePreview(null);
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Failed to submit feedback. Please try again.');
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage('Image must be under 5MB');
      return;
    }

    setImageFile(file);
    setErrorMessage('');
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (status === 'success') {
    return (
      <section className="feedback" id="feedback">
        <div className="feedback__container">
          <div className="feedback__success">
            <CheckCircle size={48} className="feedback__success-icon" />
            <h3>Thank You!</h3>
            <p>Your feedback has been submitted successfully. We appreciate you helping us improve RalliPB!</p>
            <button
              className="btn btn--primary"
              onClick={() => setStatus('idle')}
            >
              Submit More Feedback
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="feedback" id="feedback">
      <div className="feedback__container">
        <div className="section-header">
          <div className="section-badge">Support</div>
          <h2>Share Feedback or <span className="text-gradient">Get Help</span></h2>
          <p className="section-subtitle">
            Send beta feedback, report a bug, request a feature, or ask for help with the RalliPB app.
          </p>
        </div>

        <form className="feedback__form" onSubmit={handleSubmit}>
          <div className="feedback__row">
            <div className="feedback__field">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required
              />
            </div>
            <div className="feedback__field">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
              />
            </div>
          </div>

          <div className="feedback__field">
            <label>How would you rate your experience?</label>
            <div className="feedback__stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={`feedback__star ${star <= (hoverRating || rating) ? 'feedback__star--active' : ''}`}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                >
                  <Star size={28} fill={star <= (hoverRating || rating) ? '#B8FF3C' : 'none'} />
                </button>
              ))}
              <span className="feedback__rating-label">
                {rating > 0 && ['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][rating]}
              </span>
            </div>
          </div>

          <div className="feedback__field">
            <label>Feedback Category</label>
            <div className="feedback__categories">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  className={`feedback__category ${category === cat.value ? 'feedback__category--active' : ''}`}
                  onClick={() => setCategory(cat.value)}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          <div className="feedback__field">
            <label htmlFor="comments">Your Feedback</label>
            <textarea
              id="comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Tell us what you think... What do you love? What could be better? Any bugs you've encountered?"
              rows={5}
              required
            />
          </div>

          <div className="feedback__field">
            <label>Attach Screenshot (optional)</label>
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handleImageChange}
              className="feedback__file-input"
            />
            {!imagePreview && (
              <button
                type="button"
                className="feedback__upload-area"
                onClick={() => fileInputRef.current?.click()}
              >
                <ImagePlus size={24} />
                <span>Click to upload an image</span>
                <span className="feedback__upload-hint">PNG, JPG up to 5MB</span>
              </button>
            )}
            {imagePreview && (
              <div className="feedback__image-preview">
                <img src={imagePreview} alt="Preview" />
                <button
                  type="button"
                  className="feedback__image-remove"
                  onClick={removeImage}
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>

          {errorMessage && (
            <div className="feedback__error">
              <AlertCircle size={16} />
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            className="btn btn--primary btn--lg feedback__submit"
            disabled={status === 'submitting'}
          >
            {status === 'submitting' ? (
              'Submitting...'
            ) : (
              <>
                <Send size={18} />
                Submit Feedback
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
};

export default FeedbackForm;
